import type {
  BuildCategoricalChartOptionsInput,
  CategoricalChartOptions,
} from './chartBuilder.types'
import { categoryVisualSrc } from './chartCategoryAxis'
import { category, finiteNumber } from './chartData'
import { buildChartTooltip, chartFieldLabel, formatChartValue } from './chartPresentation'
import { firstField, manyFields, type ResolvedChartFields } from './resolveChartFields'

import type { ChartInstance as Chart } from '@/shared/ui/chart'

/** Renderer defaults for callers without resolved catalog presentation (direct `buildChartOptions`
 *  consumers). Catalog-driven charts take both caps from the backend row instead. */
const DEFAULT_PIE_SLICE_LIMIT = 10
const DEFAULT_MAX_PIES = 2
const SINGLE_PIE_SIZE = 280
const DUAL_PIE_SIZE = 360
const TRIPLE_PIE_SIZE = 300
/** Up to this many pies share one row under the title heading grid; more wrap into a grid. */
const MAX_PIES_PER_ROW = 3
/** Literal classes so Tailwind can see them; keyed by pies in the row. */
const HEADING_GRID_COLUMNS: Record<number, string> = { 2: 'grid-cols-2', 3: 'grid-cols-3' }
/** Four or more pies wrap into a two-column grid; each row reserves room for its own heading. */
const GRID_PIE_SIZE = 220
const GRID_HEADING_HEIGHT = 76
const GRID_ROW_HEIGHT = GRID_PIE_SIZE + GRID_HEADING_HEIGHT + 40
const GRID_LEGEND_HEIGHT = 60
const PIE_HEADING_GRID_CLASS = 'orphex-pie-heading-grid'

interface PiePoint {
  name: string
  y: number
  custom?: { visualSrc: string }
}

/**
 * The multi-metric title (`multiHeading`) is an HTML grid overlaid on the chart so each metric's
 * heading sits above its own pie (`pieLayout`'s evenly spaced single-row centers). Highcharts centers a `useHTML`
 * title by measuring the HTML's own natural width — a fixed width guessed ahead of render (the
 * former `w-[80vw] max-w-[64rem]`) essentially never matches the chart's actual pixel width, so
 * the grid's columns drifted out from under the pies whenever the rendered chart was narrower or
 * wider than that guess. Locking the grid (and the `<foreignObject>` hosting it) to the chart's
 * real `plotLeft`/`plotWidth` on every render keeps its 25%/75% column centers aligned with the
 * pies regardless of container size, and re-runs on resize since `render` fires on every redraw.
 */
function positionPieHeadingGrid(this: Chart): void {
  const titleElement = this.title.element
  const grid = titleElement.querySelector<HTMLElement>(`.${PIE_HEADING_GRID_CLASS}`)
  if (!grid || !(titleElement instanceof HTMLElement)) return
  const foreignObject = titleElement.parentElement?.parentElement
  foreignObject?.setAttribute('x', String(this.plotLeft))
  foreignObject?.setAttribute('width', String(this.plotWidth))
  titleElement.style.width = `${this.plotWidth}px`
  grid.style.width = `${this.plotWidth}px`
}

/** Share of a column (and of the plot height) a row pie may fill, leaving a gap between pies. */
const ROW_PIE_FILL = 0.88

/**
 * Row pies ask for a fixed px size that suits a wide card; on a narrower plot that size would
 * overlap the neighbouring pie. Once the chart knows its real plot box, shrink every pie to fit its
 * column (and the plot height). The equality guard makes the follow-up redraw a no-op, so this
 * settles in one extra pass and re-fits on every resize.
 */
function fitPieRow(chart: Chart, maxSize: number): void {
  const series = chart.series ?? []
  if (series.length < 2 || !chart.plotWidth || !chart.plotHeight) return
  const size = Math.floor(
    Math.min(
      maxSize,
      (chart.plotWidth / series.length) * ROW_PIE_FILL,
      chart.plotHeight * ROW_PIE_FILL,
    ),
  )
  let changed = false
  for (const item of series) {
    if ((item.options as { size?: unknown }).size === size) continue
    item.update({ type: 'pie', size }, false)
    changed = true
  }
  if (changed) chart.redraw(false)
}

function pieRowRender(maxSize: number): (this: Chart) => void {
  return function renderPieRow(this: Chart): void {
    positionPieHeadingGrid.call(this)
    fitPieRow(this, maxSize)
  }
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function gridRows(count: number): number {
  return Math.ceil(count / 2)
}

function pieLayout(
  index: number,
  count: number,
): { center: [string, string]; size: number | string } {
  if (count === 1) return { center: ['50%', '50%'], size: SINGLE_PIE_SIZE }
  if (count <= MAX_PIES_PER_ROW) {
    const x = ((index + 0.5) / count) * 100
    return {
      center: [`${Math.round(x * 100) / 100}%`, '50%'],
      size: count === 2 ? DUAL_PIE_SIZE : TRIPLE_PIE_SIZE,
    }
  }
  const rows = gridRows(count)
  const row = Math.floor(index / 2)
  // A lone pie on the last row is centred rather than left-aligned under its column.
  const lone = row === rows - 1 && count % 2 === 1
  const x = lone ? 50 : index % 2 === 0 ? 25 : 75
  const y =
    ((row * GRID_ROW_HEIGHT + GRID_HEADING_HEIGHT + GRID_PIE_SIZE / 2) / (rows * GRID_ROW_HEIGHT)) *
    100
  // Highcharts sizes a percentage pie against the smaller plot side: on a wide plot this is exactly
  // GRID_PIE_SIZE px, and on a narrow one the pies shrink with the width instead of overlapping.
  const size = (GRID_PIE_SIZE / (rows * GRID_ROW_HEIGHT)) * 100
  return {
    center: [`${x}%`, `${Math.round(y * 100) / 100}%`],
    size: `${Math.round(size * 100) / 100}%`,
  }
}

interface PieGridChart extends Chart {
  orphexPieHeadings?: Array<ReturnType<Chart['renderer']['label']>>
}

/**
 * The single-row title grid cannot reach a second row, so a grid of four or more pies draws each
 * metric heading directly above its own pie from the series' rendered center. It re-runs on every
 * redraw (resize included) and replaces the previous labels rather than stacking new ones.
 */
function pieGridHeadings(headings: readonly string[]): (this: PieGridChart) => void {
  return function drawPieGridHeadings(this: PieGridChart): void {
    for (const heading of this.orphexPieHeadings ?? []) heading.destroy()
    this.orphexPieHeadings = this.series.flatMap((series, index) => {
      const center = (series as unknown as { center?: readonly number[] }).center
      const html = headings[index]
      if (!center || !html) return []
      const [x = 0, y = 0, diameter = 0] = center
      const label = this.renderer.label(html, 0, 0, undefined, undefined, undefined, true).add()
      const box = label.getBBox()
      label.attr({
        x: this.plotLeft + x - box.width / 2,
        y: this.plotTop + y - diameter / 2 - box.height - 8,
      })
      return [label]
    })
  }
}

function pieData(
  input: BuildCategoricalChartOptionsInput,
  dimension: string,
  metric: string,
  sliceLimit: number,
): PiePoint[] {
  const sorted = input.rows
    .map((row) => {
      const name = category(row[dimension])
      const visualSrc = categoryVisualSrc(row, input.mediaDimensions ?? [])
      return {
        name,
        y: finiteNumber(row[metric]),
        ...(visualSrc ? { custom: { visualSrc } } : {}),
      }
    })
    .filter((point) => point.y !== 0)
    .sort((left, right) => right.y - left.y)
  const visible = sorted.slice(0, sliceLimit)
  const other = sorted.slice(sliceLimit).reduce((sum, point) => sum + point.y, 0)
  return other === 0
    ? visible
    : [...visible, { name: `Others (${chartFieldLabel(input, dimension)})`, y: other }]
}

function pieLegendLabel(this: {
  name?: unknown
  options?: { custom?: { visualSrc?: unknown } }
}): string {
  const label = escapeHtml(this.name)
  const visualSrc = this.options?.custom?.visualSrc
  if (typeof visualSrc !== 'string' || !visualSrc) return label
  return `<span class="inline-flex items-center gap-2xs"><img class="h-4 w-4 flex-none object-contain" src="${escapeHtml(visualSrc)}" alt=""><span>${label}</span></span>`
}

function metricTotal(
  input: BuildCategoricalChartOptionsInput,
  metric: string,
  data: readonly PiePoint[],
): number {
  const controllerTotal = Number(input.total?.[metric])
  return Number.isFinite(controllerTotal)
    ? controllerTotal
    : data.reduce((sum, point) => sum + point.y, 0)
}

function metricHeading(
  input: BuildCategoricalChartOptionsInput,
  dimension: string,
  metric: string,
  data: readonly PiePoint[],
): string {
  return [
    '<div class="grid justify-items-center">',
    `<span class="text-sm">${escapeHtml(chartFieldLabel(input, metric))}</span>`,
    `<strong class="font-numeric text-2xl font-bold">${escapeHtml(formatChartValue(input, metricTotal(input, metric, data), metric))}</strong>`,
    `<span class="text-xs font-bold">${escapeHtml(chartFieldLabel(input, dimension))}</span>`,
    '</div>',
  ].join('')
}

export function buildPieChartOptions(
  input: BuildCategoricalChartOptionsInput,
  fields: ResolvedChartFields,
  donut: boolean,
  defaultMaxPies = DEFAULT_MAX_PIES,
): CategoricalChartOptions | null {
  const dimension = firstField(fields, ['dimension', 'dimensions'])
  const metrics = manyFields(fields, ['metrics'])
  const maxPies = input.presentation?.maxSmallMultiples ?? defaultMaxPies
  if (!dimension || metrics.length === 0 || metrics.length > maxPies) return null
  const sliceLimit = input.presentation?.maxLimit ?? DEFAULT_PIE_SLICE_LIMIT

  const activeSeries = metrics.flatMap((field) => {
    const data = pieData(input, dimension, field, sliceLimit)

    return data.length > 0 ? [{ field, data }] : []
  })
  if (activeSeries.length === 0) return null
  const singleSeries = activeSeries.length === 1
  const grid = activeSeries.length > MAX_PIES_PER_ROW
  const primary = activeSeries[0]!
  const total = metricTotal(input, primary.field, primary.data)
  const headings = activeSeries.map(({ field, data }) =>
    metricHeading(input, dimension, field, data),
  )
  const multiHeading = headings.join('')

  return {
    chart: {
      type: 'pie',
      plotBorderWidth: 0,
      plotShadow: false,
      spacing: [0, 10, 0, 10],
      zooming: { type: 'xy' },
      panning: { enabled: true, type: 'xy' },
      panKey: 'shift',
      ...(grid
        ? {
            height: gridRows(activeSeries.length) * GRID_ROW_HEIGHT + GRID_LEGEND_HEIGHT,
            events: { render: pieGridHeadings(headings) },
          }
        : singleSeries
          ? {}
          : {
              events: {
                render: pieRowRender(activeSeries.length === 2 ? DUAL_PIE_SIZE : TRIPLE_PIE_SIZE),
              },
            }),
    },
    title: {
      useHTML: !singleSeries && !grid,
      text: singleSeries
        ? chartFieldLabel(input, primary.field)
        : grid
          ? ''
          : `<div class="${PIE_HEADING_GRID_CLASS} grid ${HEADING_GRID_COLUMNS[activeSeries.length]} gap-0">${multiHeading}</div>`,
      margin: 4,
      style: { fontSize: '12px', fontWeight: '700' },
    },
    subtitle: {
      useHTML: true,
      text: singleSeries
        ? `<strong class="font-numeric text-2xl font-bold">${escapeHtml(formatChartValue(input, total, primary.field))}</strong><br><span class="text-xs font-bold">${escapeHtml(chartFieldLabel(input, dimension))}</span>`
        : '',
    },
    legend: {
      enabled: true,
      useHTML: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      margin: 4,
      labelFormatter: pieLegendLabel,
      itemStyle: { color: 'var(--ui-chart-legend)', fontSize: '12px', fontWeight: '400' },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      formatter() {
        const context = this as unknown as {
          color?: unknown
          point: { color?: unknown; name: unknown; y: unknown }
          series: { index: number }
        }
        const metric = activeSeries[context.series.index]?.field ?? primary.field
        return buildChartTooltip(context.point.name, [
          {
            color: context.point.color ?? context.color,
            label: chartFieldLabel(input, metric),
            value: formatChartValue(input, context.point.y, metric),
          },
        ])
      },
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        borderColor: 'var(--ui-color-surface)',
        borderWidth: 2,
        cursor: 'pointer',
        showInLegend: singleSeries,
        dataLabels: [
          {
            distance: 20,
            enabled: singleSeries,
            crop: false,
            overflow: 'allow',
            useHTML: true,
            style: {
              fontWeight: '400',
              fontSize: '10px',
              textOverflow: 'none',
              whiteSpace: 'nowrap',
            },
            format: '<span>{point.name}</span>',
          },
          {
            enabled: true,
            distance: -40,
            format: '{point.percentage:.1f}%',
            style: { fontSize: '1.2em', textOutline: 'none', opacity: 0.7 },
            filter: { operator: '>', property: 'percentage', value: 5 },
          },
        ],
      },
    },
    series: activeSeries.map(({ field, data }, index) => {
      const layout = pieLayout(index, activeSeries.length)
      return {
        type: 'pie',
        name: chartFieldLabel(input, field),
        center: layout.center,
        size: layout.size,
        ...(!singleSeries ? { showInLegend: index === 0 } : {}),
        ...(donut ? { innerSize: '50%' } : {}),
        data,
      }
    }),
    credits: { enabled: false },
  }
}
